import * as React from "react";
import { cn } from "@/lib/utils";

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("rounded-lg border border-border bg-surface shadow-xs", className)}
      {...props}
    />
  );
}

export function CardHeader({
  className, title, description, action, ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4 border-b border-border px-4 py-3",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 space-y-0.5">
        {title && <h2 className="text-base font-semibold text-text">{title}</h2>}
        {description && <p className="text-xs text-text-muted">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-4", className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 border-t border-border bg-gray-2 px-4 py-3",
        "dark:bg-gray-1",
        className,
      )}
      {...props}
    />
  );
}

const CompactList = React.createContext(false);

/**
 * A label/value list for a narrow pane (the facts beside a record): a label column just wide enough
 * for a word or two, the same in every card so the values line up down the pane, and values that
 * wrap inside the card rather than run out of it. A `DescriptionItem` outside it keeps the wider
 * label column the dialogs and detail pages were laid out around.
 */
export function DescriptionList({ className, ...props }: React.HTMLAttributes<HTMLDListElement>) {
  return (
    <CompactList.Provider value={true}>
      <dl className={cn("divide-y divide-border", className)} {...props} />
    </CompactList.Provider>
  );
}

/** Label/value row used across the detail panes. */
export function DescriptionItem({
  label, children, className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const compact = React.useContext(CompactList);
  return (
    <div
      className={cn(
        "grid py-2",
        compact ? "grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3" : "grid-cols-[minmax(0,10rem)_1fr] gap-4",
        className,
      )}
    >
      <dt className={cn("text-xs text-text-muted", compact && "leading-5")}>{label}</dt>
      <dd className="min-w-0 break-words text-sm text-text">{children}</dd>
    </div>
  );
}
